import React from 'react';
import { GoCommentDiscussion, GoFile, GoPulse, GoSignOut, GoTerminal } from 'react-icons/go';

import Badge from 'react-bootstrap/Badge';

import SectionLabel from 'components/controls/sectionLabel';
import StatusPill from '../../components/statusPill';
import { NODE_META } from '../../domain/cellObjects';
import { isSingleExitLabel } from '../../domain/schema';

/**
 * ObjectTree — everything a cell owns, in one list.
 *
 * The order is the cell's own history: the meta-thread first because it is where the cell is
 * driven from, then one entry per `worker_task` run in log order, each with whatever that run
 * published nested beneath it. Attributing an artifact to its run rather than to the cell is the
 * whole point of the shape — "what did the second implement attempt produce" is the question a
 * person actually has.
 */
/**
 * A run's two facts, side by side: what it IS, and which way it went out.
 *
 * They were one badge, and one badge could only ever say one of them. An entry stays open from
 * dispatch until the run produces a routable label, so a run parked mid-turn on a question is still
 * open — and the tree said `running` beside a cell whose status said `input_needed`. Neither was
 * wrong; the word was carrying two jobs. Now the STATUS pill says running / completed / failed and
 * the EXIT pill says which label it took, so the reader is not asked to infer one from the other.
 *
 * `exited` is a value from a small declared set rather than free text — a step lists its `labels`
 * and every outgoing edge its `when`, and a run exits on one of them: `approved`, `changes`,
 * `rejected`, `done`, whatever the workflow's author named. Two cases have no exit to show and both
 * read as an em dash: a run still open, which has not chosen yet; and the auto-filled `ok`, which
 * the orchestrator writes for a step that CANNOT branch, so it is the one value that is not a
 * decision. A run that genuinely chose still shows what it chose.
 *
 * A closed run with no label means the run FAILED, and means nothing else. Only three paths close an
 * entry — a lifted run carries its routable exit, an answered gate carries the option a human chose,
 * and `_fail` closes with `None`. An unroutable label does not land here either: that path leaves
 * the entry OPEN and raises a gate, so the run reads as still running until somebody picks an exit.
 */
function runBadges(session) {
  const running = session.state === 'open';
  // The RUN's own status, which is not the worker's. An entry stays open from dispatch until the
  // run produces a routable label — so a run parked mid-turn on a question is still open, and the
  // tree said `running` over a cell whose status said `input_needed`. Both were true and the pair
  // read as a contradiction, because one word was standing in for two facts.
  const status = running ? 'running' : (session.label ? 'completed' : 'failed');
  // …and which exit it took. `ok` is the label the orchestrator synthesises for a step with nothing
  // to choose between, so it is the one value that is not a decision and reads as none.
  const exit = running || isSingleExitLabel(session.label) ? null : session.label;
  return (
    <span className="mc-run-badges">
      <span title="Run status"><StatusPill status={status} icon={<GoPulse />} /></span>
      <span title="Exit label">
        <Badge variant="secondary"
          className={`title-tag-badge${exit ? '' : ' title-tag-badge-muted'}`}>
          <span className="bh-pill-icon" aria-hidden="true"><GoSignOut /></span>
          {/* A run still open has not chosen yet, and `ok` is the exit that is not a decision. Both
              read as an em dash rather than as an empty pill, so the pair keeps its shape and the
              row does not reflow when a run finishes. */}
          {exit || <span aria-label="no exit yet">&mdash;</span>}
        </Badge>
      </span>
    </span>
  );
}


function ObjectTree({ tree, selectedId, onSelect }) {
  if (!tree) return null;
  const item = (id, depth, icon, label, trailing) => (
    <button
      key={id}
      type="button"
      className={`mc-tree-item${selectedId === id ? ' sel' : ''}`}
      style={{ paddingLeft: `${0.5 + depth * 0.9}rem` }}
      onClick={() => onSelect(id)}
    >
      <span className="mc-tree-icon">{icon}</span>
      <span className="mc-tree-label">{label}</span>
      {trailing && <span className="mc-tree-trailing">{trailing}</span>}
    </button>
  );

  // Two sections, split by lifetime rather than by type: GLOBAL is what lives as long as the cell
  // does, STEPS is what each run of it produced. That is the division a person actually navigates
  // by — "the cell's story" versus "what happened on attempt two".
  return (
    <div className="mc-tree">
      <SectionLabel>Global</SectionLabel>
      {/* Named the way the step threads below it are: a machine value in <code>, then the word
          "Thread". It is the only thread on a cell that is not a run's, and `meta` is the key it
          actually lives under — the alias ends `/meta`, where a run's ends `/<step>.<run>/thread`. */}
      {item(NODE_META, 0, <GoCommentDiscussion />, <><code>meta</code> Thread</>, null)}
      <SectionLabel>Steps</SectionLabel>
      {tree.sessions.map(session => (
        <React.Fragment key={session.id}>
          {/* `<step>.<run>` is DERIVED, not a name anybody chose, so it wears the app's treatment
              for a machine value — monospace <code> — rather than the alias pill, which is for
              names. "Thread" says what kind of thing it is. */}
          {item(session.id, 0, <GoTerminal />,
            <><code>{session.key}</code> Thread</>, runBadges(session))}
          {session.artifacts.map(artifact => (
            // A file path, so it wears what every other path in the app wears. It was the one
            // machine value in this tree rendered as prose.
            item(artifact.id, 1, <GoFile />, <code>{artifact.path}</code>, null)
          ))}
        </React.Fragment>
      ))}
      {tree.sessions.length === 0 && (
        <div className="mc-tree-empty">No runs yet.</div>
      )}
    </div>
  );
}

export default ObjectTree;
